/**
 * WikiConfigPanel — super-admin per-tenant wiki control.
 *
 * From one panel a super-admin can register a wiki in the catalog, edit its
 * fields, sync it, and connect/disconnect it to THIS tenant (optionally fanning
 * out to the tenant's whole subtree). Connecting enables the tenant's
 * `modules.wiki` flag AND writes the tenant_wiki row — the two gates in one action.
 */

import { useCallback, useEffect, useState } from 'react';
import api from '@services/api';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';

interface Wiki {
  wiki_id: string;
  name: string;
  slug: string;
  repo_url: string;
  ref: string;
  sync_status: string;
  sync_error?: string | null;
  credential_ref?: string | null;
  default_instructions?: string | null;
  last_synced_at?: string | null;
  is_active?: boolean;
}
interface Connection { wiki_id: string; name: string; slug: string; sync_status: string }

type WikiForm = {
  name: string; slug: string; repo_url: string; ref: string;
  credential_ref: string; default_instructions: string;
};
const EMPTY_FORM: WikiForm = { name: '', slug: '', repo_url: '', ref: 'main', credential_ref: '', default_instructions: '' };

const box: React.CSSProperties = {
  marginTop: 'var(--space-4)', padding: 'var(--space-4)',
  background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-lg)',
};
const input: React.CSSProperties = {
  width: '100%', padding: '7px 10px',
  borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 'var(--text-sm)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)',
  color: 'var(--text-primary)', marginBottom: '3px',
};
const helpStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: '3px', lineHeight: 1.4,
};
const fieldWrap: React.CSSProperties = { marginBottom: 'var(--space-3)' };

/** Labeled form field with an explanatory help line beneath. */
function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div style={fieldWrap}>
      <label style={labelStyle}>{label}</label>
      {children}
      {help && <div style={helpStyle}>{help}</div>}
    </div>
  );
}

function statusColor(status: string): string {
  if (status === 'ready') return 'var(--color-success, #16a34a)';
  if (status === 'error' || status === 'failed') return 'var(--color-error, #dc2626)';
  return 'var(--text-secondary)';
}

export default function WikiConfigPanel({ tenantId }: { tenantId: string }) {
  const { t } = useLanguage();
  const [catalog, setCatalog] = useState<Wiki[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [applyDesc, setApplyDesc] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<WikiForm>(EMPTY_FORM);
  // Inline edit state: which wiki is being edited + its draft.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<WikiForm>(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const [c, conn] = await Promise.all([
        api.get('/wiki/catalog'),
        api.get(`/wiki/tenant/${tenantId}/connections`),
      ]);
      setCatalog(c.data?.wikis || []);
      setConnections(conn.data?.connections || []);
    } catch {
      /* surfaced by individual actions */
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const isConnected = (wid: string) => connections.some((c) => c.wiki_id === wid);

  const register = async () => {
    setBusy(true);
    try {
      await api.post('/wiki/catalog', {
        name: form.name, slug: form.slug, repo_url: form.repo_url,
        ref: form.ref || 'main', credential_ref: form.credential_ref || undefined,
        default_instructions: form.default_instructions || undefined,
      });
      showNotification(t('wiki.admin.registered'), 'success');
      setShowRegister(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (e: any) {
      showNotification(e?.response?.data?.error || t('wiki.admin.syncFailed'), 'error');
    } finally { setBusy(false); }
  };

  const beginEdit = (w: Wiki) => {
    setEditingId(w.wiki_id);
    setEditForm({
      name: w.name || '', slug: w.slug || '', repo_url: w.repo_url || '',
      ref: w.ref || 'main', credential_ref: w.credential_ref || '',
      default_instructions: w.default_instructions || '',
    });
  };

  const saveEdit = async (wid: string) => {
    setBusy(true);
    try {
      await api.patch(`/wiki/catalog/${wid}`, {
        name: editForm.name,
        repo_url: editForm.repo_url,
        ref: editForm.ref || 'main',
        credential_ref: editForm.credential_ref || null,
        default_instructions: editForm.default_instructions || null,
      });
      showNotification(t('wiki.admin.updated'), 'success');
      setEditingId(null);
      await load();
    } catch (e: any) {
      showNotification(e?.response?.data?.error || t('wiki.admin.syncFailed'), 'error');
    } finally { setBusy(false); }
  };

  const sync = async (wid: string) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/wiki/catalog/${wid}/sync`, {});
      showNotification(data?.status === 'ready' ? t('wiki.admin.synced') : (data?.error || t('wiki.admin.syncFailed')),
        data?.status === 'ready' ? 'success' : 'error');
      await load();
    } catch (e: any) {
      // A 5xx body is generic by design (request_guards); the wiki's own row
      // carries why the sync failed, so show the row again.
      const res = e?.response;
      showNotification((res?.status < 500 && res?.data?.error) || t('wiki.admin.syncFailed'), 'error');
      await load();
    } finally { setBusy(false); }
  };

  const toggleConnect = async (wid: string, connect: boolean) => {
    setBusy(true);
    try {
      const path = connect ? 'connect' : 'disconnect';
      await api.post(`/wiki/catalog/${wid}/${path}`, { tenant_id: tenantId, apply_to_descendants: applyDesc });
      showNotification(t('wiki.admin.connected'), 'success');
      await load();
    } catch (e: any) {
      showNotification(e?.response?.data?.error || 'Error', 'error');
    } finally { setBusy(false); }
  };

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-1)' }}>
        <div style={{ fontWeight: 'var(--font-medium)' }}>{t('wiki.admin.title')}</div>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setShowRegister((s) => !s)}>
          {showRegister ? t('wiki.admin.cancel') : t('wiki.admin.register')}
        </button>
      </div>
      <div style={{ ...helpStyle, marginTop: 0, marginBottom: 'var(--space-3)' }}>{t('wiki.admin.intro')}</div>

      {showRegister && (
        <div style={{ marginBottom: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
          <Field label={t('wiki.admin.name')} help={t('wiki.admin.nameHelp')}>
            <input dir="auto" style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label={t('wiki.admin.slug')} help={t('wiki.admin.slugHelp')}>
            <input dir="auto" style={input} placeholder="e.g. sacks-wiki" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          </Field>
          <Field label={t('wiki.admin.repoUrl')} help={t('wiki.admin.repoUrlHelp')}>
            <input dir="auto" style={input} placeholder="https://github.com/org/repo" value={form.repo_url} onChange={(e) => setForm({ ...form, repo_url: e.target.value })} />
          </Field>
          <Field label={t('wiki.admin.ref')} help={t('wiki.admin.refHelp')}>
            <input dir="auto" style={input} value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} />
          </Field>
          <Field label={t('wiki.admin.credentialRef')} help={t('wiki.admin.credentialRefHelp')}>
            <input dir="auto" style={input} placeholder="WIKI_TOKEN_SACKS" value={form.credential_ref} onChange={(e) => setForm({ ...form, credential_ref: e.target.value })} />
          </Field>
          <Field label={t('wiki.admin.defaultInstructions')} help={t('wiki.admin.defaultInstructionsHelp')}>
            <textarea dir="auto" style={{ ...input, minHeight: 64, resize: 'vertical' }} value={form.default_instructions} onChange={(e) => setForm({ ...form, default_instructions: e.target.value })} />
          </Field>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !form.name || !form.slug || !form.repo_url} onClick={register}>
            {t('wiki.admin.create')}
          </button>
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
        <input type="checkbox" checked={applyDesc} onChange={(e) => setApplyDesc(e.target.checked)} />
        {t('wiki.admin.applyToSubtenants')}
      </label>

      {catalog.length === 0 && <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{t('wiki.admin.empty')}</div>}

      {catalog.map((w) => (
        <div key={w.wiki_id} style={{ padding: 'var(--space-3) 0', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                {w.name}
                {isConnected(w.wiki_id) && (
                  <span style={{ marginInlineStart: 8, fontSize: 'var(--text-xs)', color: 'var(--color-success, #16a34a)', fontWeight: 'var(--font-medium)' }}>
                    ● {t('wiki.admin.connected')}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 2 }}>
                <code>{w.slug}</code> · {t('wiki.admin.syncStatus')}:{' '}
                <span style={{ color: statusColor(w.sync_status) }}>{w.sync_status}</span>
                {w.last_synced_at && <> · {t('wiki.admin.lastSynced')}: {new Date(w.last_synced_at).toLocaleString()}</>}
              </div>
              {w.sync_error && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error, #dc2626)', marginTop: 2 }} dir="auto">
                  {t('wiki.admin.syncError')}: {w.sync_error}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => (editingId === w.wiki_id ? setEditingId(null) : beginEdit(w))}>
                {editingId === w.wiki_id ? t('wiki.admin.cancel') : t('wiki.admin.edit')}
              </button>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => sync(w.wiki_id)}>{t('wiki.admin.syncNow')}</button>
              {isConnected(w.wiki_id) ? (
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => toggleConnect(w.wiki_id, false)}>{t('wiki.admin.disconnect')}</button>
              ) : (
                <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => toggleConnect(w.wiki_id, true)}>{t('wiki.admin.connect')}</button>
              )}
            </div>
          </div>

          {editingId === w.wiki_id && (
            <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
              <Field label={t('wiki.admin.name')} help={t('wiki.admin.nameHelp')}>
                <input dir="auto" style={input} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </Field>
              <Field label={t('wiki.admin.slug')} help={t('wiki.admin.slugImmutableHelp')}>
                <input dir="auto" style={{ ...input, opacity: 0.6, cursor: 'not-allowed' }} value={editForm.slug} disabled readOnly />
              </Field>
              <Field label={t('wiki.admin.repoUrl')} help={t('wiki.admin.repoUrlHelp')}>
                <input dir="auto" style={input} value={editForm.repo_url} onChange={(e) => setEditForm({ ...editForm, repo_url: e.target.value })} />
              </Field>
              <Field label={t('wiki.admin.ref')} help={t('wiki.admin.refHelp')}>
                <input dir="auto" style={input} value={editForm.ref} onChange={(e) => setEditForm({ ...editForm, ref: e.target.value })} />
              </Field>
              <Field label={t('wiki.admin.credentialRef')} help={t('wiki.admin.credentialRefHelp')}>
                <input dir="auto" style={input} value={editForm.credential_ref} onChange={(e) => setEditForm({ ...editForm, credential_ref: e.target.value })} />
              </Field>
              <Field label={t('wiki.admin.defaultInstructions')} help={t('wiki.admin.defaultInstructionsHelp')}>
                <textarea dir="auto" style={{ ...input, minHeight: 64, resize: 'vertical' }} value={editForm.default_instructions} onChange={(e) => setEditForm({ ...editForm, default_instructions: e.target.value })} />
              </Field>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button type="button" className="btn btn-sm btn-primary" disabled={busy || !editForm.name || !editForm.repo_url} onClick={() => saveEdit(w.wiki_id)}>
                  {t('wiki.admin.save')}
                </button>
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setEditingId(null)}>{t('wiki.admin.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
